import { useParams, Link } from 'react-router-dom';
import { getComicById, comics, categories } from '@/mocks/comics';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import SEOSchema from '@/components/base/SEOSchema';
import { useMemo } from 'react';

export default function ComicPage() {
  const { id } = useParams<{ id: string }>();
  const comic = id ? getComicById(id) : null;

  const relatedComics = useMemo(() => {
    if (!comic) return [];
    return comics
      .filter((c) => c.id !== comic.id && c.tags.some((tag) => comic.tags.includes(tag)))
      .slice(0, 4);
  }, [comic]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <div className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">漫画不存在</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const categoryName = categories.find((c) => c.slug === comic.category)?.name || comic.category;

  return (
    <>
      <SEOSchema type="Article" data={{
        headline: `${comic.title} - 香蕉漫画`,
        description: comic.description,
        author: { '@type': 'Person', name: comic.author },
        datePublished: comic.publishedAt,
        dateModified: comic.updatedAt,
        mainEntityOfPage: { '@type': 'WebPage', '@id': `https://banana-comic.com/comic/${comic.id}` },
        publisher: { '@type': 'Organization', name: '香蕉漫画', url: 'https://banana-comic.com' },
      }} />
      <SEOSchema type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://banana-comic.com/' },
          { '@type': 'ListItem', position: 2, name: categoryName, item: `https://banana-comic.com/category/${comic.category}` },
          { '@type': 'ListItem', position: 3, name: comic.title, item: `https://banana-comic.com/comic/${comic.id}` },
        ],
      }} />
      {comic.faq.length > 0 && (
        <SEOSchema type="FAQPage" data={{
          mainEntity: comic.faq.map((f) => ({
            '@type': 'Question',
            name: f.question,
            acceptedAnswer: { '@type': 'Answer', text: f.answer },
          })),
        }} />
      )}

      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={[
          { label: categoryName, href: `/category/${comic.category}` },
          { label: comic.title },
        ]} />

        <article className="max-w-5xl mx-auto px-4 md:px-6 py-8">
          {/* Header */}
          <div className="flex flex-col lg:flex-row gap-8 mb-12">
            <div className="flex-shrink-0 w-full lg:w-[280px]">
              <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
                <img
                  src={comic.coverImage}
                  alt={`${comic.title} - ${comic.tags.join('、')}漫画封面`}
                  title={`${comic.title} - 漫画推荐`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium whitespace-nowrap">
                  {categoryName}
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                  comic.status === '已完结' ? 'bg-secondary-100 text-secondary-800' : 'bg-primary-100 text-primary-700'
                }`}>
                  {comic.status}
                </span>
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                {comic.title}
              </h1>
              <p className="text-sm text-foreground-500 mb-4">{comic.altTitle}</p>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6 p-4 rounded-lg bg-background-100">
                <div>
                  <span className="text-xs text-foreground-500">作者</span>
                  <p className="font-medium text-foreground-900 text-sm">{comic.author}</p>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">画师</span>
                  <p className="font-medium text-foreground-900 text-sm">{comic.artist}</p>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">评分</span>
                  <p className="font-medium text-foreground-900 text-sm flex items-center gap-1">
                    <i className="ri-star-fill text-secondary-500"></i>
                    {comic.rating}
                  </p>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">总话数</span>
                  <p className="font-medium text-foreground-900 text-sm">{comic.chapters}话</p>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">阅读量</span>
                  <p className="font-medium text-foreground-900 text-sm">{(comic.views / 10000).toFixed(0)}万</p>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">收藏数</span>
                  <p className="font-medium text-foreground-900 text-sm">{(comic.favorites / 10000).toFixed(0)}万</p>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">发布时间</span>
                  <p className="font-medium text-foreground-900 text-sm">{comic.publishedAt}</p>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">最近更新</span>
                  <p className="font-medium text-foreground-900 text-sm">{comic.updatedAt}</p>
                </div>
              </div>

              <p className="text-foreground-700 leading-relaxed mb-6">{comic.summary}</p>

              <div className="flex items-center gap-3 flex-wrap">
                <button className="px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-book-read-line mr-1.5"></i>开始阅读
                </button>
                <button className="px-6 py-2.5 rounded-lg border border-primary-300 text-primary-600 font-medium text-sm hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-bookmark-line mr-1.5"></i>加入收藏
                </button>
              </div>
            </div>
          </div>

          {/* Highlights */}
          {comic.highlights.length > 0 && (
            <div className="mb-12 p-6 rounded-lg bg-accent-100/70 border border-accent-200/60">
              <h2 className="font-heading text-lg font-bold text-foreground-900 mb-4">作品亮点</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {comic.highlights.map((h, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-accent-500"></i>
                    </div>
                    <span className="text-sm text-foreground-800">{h}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Full Content */}
          <div className="mb-12">
            <div
              className="prose prose-sm max-w-none text-foreground-800 leading-relaxed [&_h2]:font-heading [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-foreground-900 [&_h2]:mb-4 [&_h2]:mt-8 [&_p]:mb-4 [&_strong]:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: comic.fullContent }}
            />
          </div>

          {/* FAQ */}
          {comic.faq.length > 0 && (
            <div className="mb-12 p-6 rounded-lg bg-background-100 border border-background-200/70">
              <h2 className="font-heading text-lg font-bold text-foreground-900 mb-4">常见问题</h2>
              <div className="flex flex-col gap-3">
                {comic.faq.map((f, i) => (
                  <details key={i} className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden">
                    <summary className="flex items-center justify-between px-4 py-3 cursor-pointer list-none">
                      <h3 className="font-medium text-foreground-900 text-sm pr-8">{f.question}</h3>
                      <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <div className="px-4 pb-3 text-sm text-foreground-600 leading-relaxed">{f.answer}</div>
                  </details>
                ))}
              </div>
            </div>
          )}

          {/* Related Comics */}
          {relatedComics.length > 0 && (
            <div className="mb-12">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </div>
          )}

          {/* Back button */}
          <div className="text-center pt-4 border-t border-background-200/70">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-foreground-600 hover:text-primary-500 transition-colors text-sm whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页，发现更多漫画
            </Link>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}